import { useMemo, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type TableOptions,
  type RowSelectionState,
  type OnChangeFn,
} from '@tanstack/react-table';
import {
  CaretDown,
  CaretUp,
  CaretSort,
  CaretLeft,
  CaretRight,
} from '@carbon/icons-react';
import { Authorizor } from '../Authorizor';
import { useOptionalAuth } from '../../../hooks/use-auth';
import { SkeletonLoader } from '../loaders/skeleton-loader/skeleton-loader';
import { SelectDropdown } from '../select-dropdown/select-dropdown';
import { Button, Checkbox } from '@/components/ui';
import './table-style.scss';

// Extend ColumnMeta to include allowedRoles and custom header className
declare module '@tanstack/react-table' {
  interface ColumnMeta<TData, TValue> {
    allowedRoles?: string[];
    headerClassName?: string;
    cellClassName?: string;
    preventRowClick?: boolean;
  }
}

export interface TableData<TData> {
  data: TData[];
  totalCount: number;
  pageCount: number;
  currentPageIndex: number;
  currentPageSize?: number; // Optional: Current page size from pagination state
}

export interface PaginationConfig {
  enabled?: boolean;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
}

export interface TableSelectionConfig<TData> {
  // Per-row eligibility → drives enableRowSelection + the checkbox disabled state.
  canSelectRow?: (row: TData) => boolean;
  // Tooltip shown on a disabled checkbox (e.g. why the row can't be selected).
  getDisabledReason?: (row: TData) => string | undefined;
  // Who sees the checkbox column (column-level RBAC, same mechanism as other columns).
  allowedRoles?: string[];
  // While a bulk action is running, disable every checkbox.
  isApplying?: boolean;
}

export interface TableProps<TData> {
  id?: string; // Unique ID for the table container
  className?: string; // Optional extra class(es) on the table container (for scoped styling)
  columns: ColumnDef<TData>[]; // Column definitions
  tableData: TableData<TData>; // Server-side pagination data
  isDataLoading?: boolean; // Boolean to show a loading skeleton while fetching data
  onRowClick?: (row: { original: TData }) => void; // Optional: Function to handle row click events
  sortConfig?: {
    // Optional: Default sorting configuration -> { field: "columnId", order: "asc" | "desc" }
    field: string;
    order: 'asc' | 'desc';
  };
  paginationConfig?: PaginationConfig; // Optional: Pagination configuration
  searchText?: string; // Current search text value
  setPaginationState: (state: {
    // Optional: Function to set the pagination state
    page: number;
    pageSize: number;
    sortBy: string;
    direction: 'asc' | 'desc';
    searchText: string;
  }) => void;
  // Opt-in row selection (controlled). Selection activates only when
  // rowSelection + onRowSelectionChange + getRowId are all provided; otherwise
  // the table renders exactly as before (no checkbox column).
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  getRowId?: (row: TData) => string;
  selectionConfig?: TableSelectionConfig<TData>;
}

const Table = <TData,>({
  id = 'table',
  className,
  columns,
  tableData,
  isDataLoading = false,
  onRowClick,
  sortConfig,
  paginationConfig = {
    enabled: true,
    defaultPageSize: 10,
    pageSizeOptions: [5, 10, 25, 50, 100],
  },
  searchText = '',
  setPaginationState,
  rowSelection,
  onRowSelectionChange,
  getRowId,
  selectionConfig,
}: TableProps<TData>) => {
  const { data, totalCount, pageCount, currentPageIndex, currentPageSize } =
    tableData;

  const isSelectionEnabled = Boolean(
    rowSelection && onRowSelectionChange && getRowId,
  );

  const selectColumn: ColumnDef<TData> = {
    id: 'select',
    header: '',
    enableSorting: false,
    meta: {
      allowedRoles: selectionConfig?.allowedRoles,
      headerClassName: 'select-column',
      cellClassName: 'select-column',
      preventRowClick: true,
    },
    cell: ({ row }) => {
      const disabled =
        !row.getCanSelect() || Boolean(selectionConfig?.isApplying);
      const reason = selectionConfig?.getDisabledReason?.(row.original);
      return (
        <span title={reason}>
          <Checkbox
            checked={row.getIsSelected()}
            disabled={disabled}
            onCheckedChange={() => row.toggleSelected()}
          />
        </span>
      );
    },
  };

  const effectiveColumns = isSelectionEnabled
    ? [selectColumn, ...columns]
    : columns;

  // <Authorizor> strips role-hidden th/td from the DOM, but TanStack still
  // counts those columns. Spanning the full count on the loading and
  // empty-state rows makes the browser synthesise phantom columns — worst
  // case colSpan={11} over a Driver's 4 real cells — which inflates the
  // table's width and produces a transient horizontal scrollbar. Mirror
  // Authorizor's own check (including its render-nothing-while-loading
  // behaviour) so the span matches what is actually in the DOM.
  // Optional on purpose: a table whose columns are not role-gated renders
  // perfectly well with no AuthProvider above it, and requiring one here would
  // make every such caller supply one. Columns that *are* role-gated already
  // depend on a provider via <Authorizor>.
  const auth = useOptionalAuth();
  const authorizedRole = auth?.isLoading ? undefined : auth?.user?.role;
  const visibleColumnCount = Math.max(
    1,
    effectiveColumns.filter((column) => {
      const allowedRoles = column.meta?.allowedRoles;
      if (!allowedRoles?.length) return true;
      return (
        authorizedRole !== undefined && allowedRoles.includes(authorizedRole)
      );
    }).length,
  );

  // Extract pagination config with defaults
  const {
    enabled: isPaginationEnabled = true,
    defaultPageSize = 10,
    pageSizeOptions = [5, 10, 25, 50, 100],
  } = paginationConfig;

  // Use currentPageSize from props if available, otherwise use defaultPageSize
  const initialPageSize = currentPageSize || defaultPageSize;

  // Convert 1-based currentPageIndex to 0-based for TanStack Table
  const tablePageIndex = currentPageIndex > 0 ? currentPageIndex - 1 : 0;

  // Initial sort configuration
  const initialSortBy = useMemo(() => {
    if (sortConfig?.field) {
      return [
        {
          id: sortConfig.field,
          desc: sortConfig.order === 'desc',
        },
      ];
    }
    return [];
  }, [sortConfig?.field, sortConfig?.order]);

  const tableOptions: TableOptions<TData> = {
    data,
    columns: effectiveColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    enableSorting: true,
    pageCount,
    autoResetPageIndex: false,
    ...(isSelectionEnabled
      ? {
          getRowId: (row: TData) => getRowId!(row),
          enableRowSelection: (row) =>
            selectionConfig?.canSelectRow
              ? selectionConfig.canSelectRow(row.original)
              : true,
          enableMultiRowSelection: true,
          state: { rowSelection },
          onRowSelectionChange,
        }
      : {}),
    initialState: {
      pagination: {
        pageIndex: tablePageIndex,
        pageSize: initialPageSize,
      },
      sorting: initialSortBy,
    },
  };

  const table = useReactTable(tableOptions);

  const { pageIndex, pageSize } = table.getState().pagination;
  const sorting = table.getState().sorting;

  // Extract sort values for dependency tracking
  const sortBy = sorting[0]?.id || sortConfig?.field || '';
  const sortDirection = sorting[0]?.desc ? 'desc' : 'asc';

  // Sync with external sortConfig changes
  useEffect(() => {
    if (sortConfig?.field) {
      table.setSorting([
        { id: sortConfig.field, desc: sortConfig.order === 'desc' },
      ]);
    }
  }, [sortConfig?.field, sortConfig?.order, table]);

  // Update pagination state when table state changes (matching old implementation pattern)
  useEffect(() => {
    setPaginationState({
      page: pageIndex + 1, // Convert 0-based to 1-based
      pageSize,
      sortBy,
      direction: sortDirection,
      searchText, // Preserve the current search text
    });
  }, [pageIndex, pageSize, sortBy, sortDirection]); // Don't include setPaginationState or searchText to avoid loops

  // Sync with external currentPageIndex changes
  useEffect(() => {
    if (currentPageIndex > 0 && tablePageIndex !== pageIndex) {
      table.setPageIndex(tablePageIndex);
    }
  }, [currentPageIndex]);

  // Handle sorting
  const handleSort = (columnId: string) => {
    const currentSort = sorting.find((s) => s.id === columnId);
    const isDesc = currentSort ? !currentSort.desc : false;

    table.setSorting([{ id: columnId, desc: isDesc }]);
    table.setPageIndex(0); // Reset to first page on sort
  };

  // Calculate displayed entries
  const startEntry = totalCount > 0 ? pageIndex * pageSize + 1 : 0;
  const endEntry =
    totalCount > 0 ? Math.min((pageIndex + 1) * pageSize, totalCount) : 0;

  return (
    <div
      className={`table-container ${className ?? ''}`.trim()}
      id={`${id}-container`}
    >
      {/* Number of entries info - top left */}
      {isPaginationEnabled && totalCount > 0 && (
        <div className="pagination-entries-info">
          Showing results {startEntry}-{endEntry} out of {totalCount}
        </div>
      )}

      <div className="table-wrapper">
        <table id={`${id}-table`} className="full-view-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const isSorted = header.column.getIsSorted();
                  const isSortedDesc = isSorted === 'desc';

                  const customHeaderClass =
                    header.column.columnDef.meta?.headerClassName || '';

                  const content = (
                    <th
                      key={header.id}
                      onClick={() => canSort && handleSort(header.column.id)}
                      className={`${
                        isSorted ? 'sorted-column' : ''
                      } ${customHeaderClass}`.trim()}
                      style={{
                        cursor: canSort ? 'pointer' : 'default',
                      }}
                    >
                      {/* The label needs a real element (not a bare text node)
                          so stylesheets can target it — an anonymous flex item
                          can't be selected, and a line clamp can't go on the
                          `th` without destroying `display: table-cell`. */}
                      <div className="table-th-content">
                        <span className="table-th-label">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </span>
                        {canSort && (
                          <span className="table-th-caret">
                            {isSorted ? (
                              isSortedDesc ? (
                                <CaretDown className="small-icon" />
                              ) : (
                                <CaretUp className="small-icon" />
                              )
                            ) : (
                              <CaretSort className="small-icon opacity-50" />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  );

                  // Data will be wrapped by Authorizor if allowed roles provided else it will be displayed as it is
                  return header.column.columnDef.meta?.allowedRoles?.length ? (
                    <Authorizor
                      key={header.id}
                      allowedRoles={header.column.columnDef.meta.allowedRoles}
                    >
                      {content}
                    </Authorizor>
                  ) : (
                    content
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {isDataLoading ? (
              <tr>
                <td colSpan={visibleColumnCount}>
                  <SkeletonLoader count={pageSize} height={30} />
                </td>
              </tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={visibleColumnCount} className="p-5 text-center">
                  <div className="table-no-results-text">No results found</div>
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={onRowClick ? 'table-hoverable-row' : ''}
                  onClick={() => onRowClick && onRowClick(row)}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isCellSorted = cell.column.getIsSorted();
                    const customCellClassName =
                      cell.column.columnDef.meta?.cellClassName || '';
                    const content = (
                      <td
                        key={cell.id}
                        className={`${
                          isCellSorted ? 'sorted-column' : ''
                        } ${customCellClassName}`.trim()}
                        onClick={
                          cell.column.columnDef.meta?.preventRowClick
                            ? (e) => e.stopPropagation()
                            : undefined
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    );

                    // Data will be wrapped by Authorizor if allowed roles provided else it will be displayed as it is
                    return cell.column.columnDef.meta?.allowedRoles?.length ? (
                      <Authorizor
                        key={cell.id}
                        allowedRoles={cell.column.columnDef.meta.allowedRoles}
                      >
                        {content}
                      </Authorizor>
                    ) : (
                      content
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {isPaginationEnabled && (
        <div className="pagination-wrapper">
          <div className="pagination-container">
            {/* Dropdown for number of rows per page */}
            <SelectDropdown
              id={`${id}-pagination-page-size-dropdown`}
              value={pageSize.toString()}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
                table.setPageIndex(0); // Reset to first page when changing page size
              }}
              borderless={true}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size.toString()}>
                  {size}
                </option>
              ))}
            </SelectDropdown>
            <span className="pagination-text">Rows per page</span>
            <span className="pagination-separator"></span>

            {/* Dropdown for page number */}
            <SelectDropdown
              id={`${id}-pagination-page-number-dropdown`}
              value={pageIndex.toString()}
              onChange={(e) => table.setPageIndex(Number(e.target.value))}
              borderless={true}
            >
              {pageCount > 0 ? (
                Array.from({ length: pageCount }, (_, i) => (
                  <option key={i} value={i.toString()}>
                    {i + 1}
                  </option>
                ))
              ) : (
                <option value="0">1</option>
              )}
            </SelectDropdown>
            <span className="pagination-text">of {pageCount || 1} pages</span>

            {/* Buttons to navigate to previous and next pages */}
            <Button
              id={`${id}-pagination-previous-page`}
              variant="icon-primary"
              size="small"
              className="pagination-icon-button"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <CaretLeft className="small-icon brand-icon" />
            </Button>
            <Button
              id={`${id}-pagination-next-page`}
              variant="icon-primary"
              size="small"
              className="pagination-icon-button"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <CaretRight className="small-icon brand-icon" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export { Table };
